import {
  ArrowLeft,
  Download,
  Plus,
  RefreshCw,
  Search,
  UserCheck,
  Users,
} from "lucide-react";
import { twMerge } from "tailwind-merge";
import type { RfpPresenceActiveUser, RfpStatus } from "../../types/rfp";

type RfpQuestionAnswersHeaderProps = {
  readonly title: string;
  readonly status: RfpStatus | string;
  readonly dueDate: string | null;
  readonly ownerName?: string;
  readonly uploadedByName?: string;
  readonly searchInput: string;
  readonly onSearchChange: (value: string) => void;
  readonly assignedToMeOnly?: boolean;
  readonly onAssignedToMeOnlyChange?: (value: boolean) => void;
  readonly onBack: () => void;
  readonly onAssignQuestions?: () => void;
  readonly onAddQuestion?: () => void;
  readonly onSyncPending?: () => void;
  readonly syncEligibleCount?: number;
  readonly isSyncing?: boolean;
  readonly onExport?: () => void;
  readonly isExporting?: boolean;
  readonly activeUsers?: readonly RfpPresenceActiveUser[];
};

const OUTLINE_BUTTON =
  "inline-flex shrink-0 items-center justify-center gap-1.5 rounded-md border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50";

const PRIMARY_BUTTON =
  "inline-flex shrink-0 items-center justify-center gap-1.5 rounded-md bg-primary-500 px-3 py-2 text-sm font-medium text-white transition-colors hover:bg-primary-600 disabled:cursor-not-allowed disabled:opacity-60";

const formatDate = (iso: string | null): string => {
  if (!iso) return "—";
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return "—";
  return date.toLocaleDateString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
};

const normalizeStatusLabel = (status: string): string =>
  status
    .split("_")
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");

const formatDisplayTitle = (title: string): string => {
  const trimmed = title.trim();
  if (trimmed.length <= 52) return trimmed;

  const uuidMatch =
    /^([0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})(.*)$/i.exec(
      trimmed,
    );
  if (uuidMatch) {
    const [, uuid, suffix] = uuidMatch;
    return `${uuid.slice(0, 8)}…${uuid.slice(-4)}${suffix}`;
  }

  return `${trimmed.slice(0, 49)}…`;
};

const buildMetadataLine = (
  ownerName: string,
  uploadedByName: string,
  dueDate: string | null,
): string => {
  const parts: string[] = [];
  if (ownerName !== "—") {
    parts.push(ownerName);
  }
  if (
    uploadedByName !== "—" &&
    uploadedByName.trim() &&
    uploadedByName !== ownerName
  ) {
    parts.push(`Uploaded by ${uploadedByName}`);
  }
  parts.push(`Due ${formatDate(dueDate)}`);
  return parts.join(" · ");
};

const AVATAR_BG_CLASSES = [
  "bg-sky-500",
  "bg-emerald-500",
  "bg-amber-500",
  "bg-violet-500",
] as const;

const getUserInitials = (fullName: string): string => {
  const words = fullName
    .trim()
    .split(/\s+/)
    .filter(Boolean);
  if (words.length === 0) return "?";
  if (words.length === 1) return words[0].slice(0, 2).toUpperCase();
  return `${words[0][0] ?? ""}${words[1][0] ?? ""}`.toUpperCase();
};

const ActiveUsersStack = ({
  activeUsers,
}: {
  readonly activeUsers: readonly RfpPresenceActiveUser[];
}) => {
  const visibleUsers = activeUsers.slice(0, 4);
  const overflowCount = Math.max(0, activeUsers.length - visibleUsers.length);

  return (
    <div className="hidden items-center gap-2 xl:flex" title="Users viewing this RFP">
      <span className="text-xs text-gray-400">Active</span>
      <div className="flex -space-x-2">
        {activeUsers.length === 0 ? (
          <div className="flex h-7 w-7 items-center justify-center rounded-full border-2 border-white bg-gray-300 text-[11px] font-semibold text-white">
            0
          </div>
        ) : (
          <>
            {visibleUsers.map((user, index) => (
              <div
                key={user.user_id}
                title={user.full_name}
                className={twMerge(
                  "flex h-7 w-7 items-center justify-center rounded-full border-2 border-white text-xs font-semibold text-white",
                  AVATAR_BG_CLASSES[index % AVATAR_BG_CLASSES.length],
                )}
              >
                {getUserInitials(user.full_name)}
              </div>
            ))}
            {overflowCount > 0 ? (
              <div className="flex h-7 w-7 items-center justify-center rounded-full border-2 border-white bg-gray-200 text-[11px] font-semibold text-gray-700">
                +{overflowCount}
              </div>
            ) : null}
          </>
        )}
      </div>
    </div>
  );
};

export const RfpQuestionAnswersHeader = ({
  title,
  status,
  dueDate,
  ownerName = "—",
  uploadedByName = "—",
  searchInput,
  onSearchChange,
  assignedToMeOnly = false,
  onAssignedToMeOnlyChange,
  onBack,
  onAssignQuestions,
  onAddQuestion,
  onSyncPending,
  syncEligibleCount = 0,
  isSyncing = false,
  onExport,
  isExporting = false,
  activeUsers = [],
}: RfpQuestionAnswersHeaderProps) => {
  const displayTitle = formatDisplayTitle(title);
  const metadataLine = buildMetadataLine(ownerName, uploadedByName, dueDate);
  const metadataTitle =
    uploadedByName !== "—" && uploadedByName !== ownerName
      ? `Owner: ${ownerName} · Uploaded by: ${uploadedByName} · Due: ${formatDate(dueDate)}`
      : `Owner: ${ownerName} · Due: ${formatDate(dueDate)}`;

  const syncTitle =
    syncEligibleCount === 0
      ? "No questions need answer retrieval"
      : "Generate AI answers for pending questions";

  return (
    <header className="sticky top-0 z-10 border-b border-gray-200 bg-white">
      <div className="flex items-start justify-between gap-4 px-6 pb-2 pt-4">
        <div className="flex min-w-0 flex-1 items-start gap-3">
          <button
            type="button"
            onClick={onBack}
            className="mt-0.5 shrink-0 rounded-md p-1 text-gray-500 transition-colors hover:bg-gray-100 hover:text-gray-800"
            aria-label="Back to My RFPs"
          >
            <ArrowLeft className="h-5 w-5" />
          </button>
          <div className="min-w-0 flex-1">
            <div className="flex min-w-0 flex-wrap items-center gap-2">
              <h1
                className="min-w-0 truncate text-lg font-semibold text-heading"
                title={title}
              >
                {displayTitle}
              </h1>
              <span className="shrink-0 rounded bg-primary-50 px-2 py-0.5 text-xs font-semibold text-primary-600">
                {normalizeStatusLabel(status)}
              </span>
            </div>
            <p
              className="mt-0.5 truncate text-xs text-gray-500"
              title={metadataTitle}
            >
              {metadataLine}
            </p>
          </div>
        </div>

        <div className="flex shrink-0 items-center gap-3">
          <ActiveUsersStack activeUsers={activeUsers} />
          {onExport ? (
            <button
              type="button"
              onClick={onExport}
              disabled={isExporting}
              className={twMerge(PRIMARY_BUTTON, "hidden sm:inline-flex")}
              aria-busy={isExporting}
            >
              <Download className="h-4 w-4" />
              {isExporting ? "Exporting..." : "Export"}
            </button>
          ) : null}
        </div>
      </div>

      <div className="hidden items-center justify-between gap-4 border-t border-gray-100 px-6 py-2.5 lg:flex">
        <div className="flex min-w-0 flex-1 items-center gap-3">
          <div className="relative min-w-0 flex-1 max-w-sm">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-500" />
            <input
              type="text"
              value={searchInput}
              onChange={(event) => onSearchChange(event.target.value)}
              placeholder="Search questions..."
              className="w-full rounded-md border border-gray-300 py-2 pl-9 pr-3 text-sm text-body focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
            />
          </div>
          {onAssignedToMeOnlyChange ? (
            <button
              type="button"
              onClick={() => onAssignedToMeOnlyChange(!assignedToMeOnly)}
              aria-pressed={assignedToMeOnly}
              className={twMerge(
                OUTLINE_BUTTON,
                assignedToMeOnly &&
                  "border-primary-500 bg-primary-50 text-primary-700 hover:bg-primary-50",
              )}
            >
              <UserCheck className="h-4 w-4" />
              Assigned to me
            </button>
          ) : null}
        </div>

        <div className="flex shrink-0 items-center gap-2">
          {onAddQuestion ? (
            <button type="button" onClick={onAddQuestion} className={OUTLINE_BUTTON}>
              <Plus className="h-4 w-4" />
              Add question
            </button>
          ) : null}
          {onSyncPending ? (
            <button
              type="button"
              onClick={onSyncPending}
              disabled={syncEligibleCount === 0 || isSyncing}
              className={OUTLINE_BUTTON}
              title={syncTitle}
            >
              <RefreshCw className={`h-4 w-4 ${isSyncing ? "animate-spin" : ""}`} />
              {isSyncing ? (
                "Syncing..."
              ) : (
                <span className="inline-flex items-center gap-1.5">
                  Sync
                  {syncEligibleCount > 0 ? (
                    <span className="rounded-full bg-gray-200 px-1.5 py-0.5 text-xs font-semibold leading-none text-gray-700">
                      {syncEligibleCount}
                    </span>
                  ) : null}
                </span>
              )}
            </button>
          ) : null}
          {onAssignQuestions ? (
            <button
              type="button"
              onClick={onAssignQuestions}
              className={OUTLINE_BUTTON}
            >
              <Users className="h-4 w-4" />
              Assign
            </button>
          ) : null}
        </div>
      </div>

      <div className="space-y-2 px-6 pb-3 lg:hidden">
        <div className="relative">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-500" />
          <input
            type="text"
            value={searchInput}
            onChange={(event) => onSearchChange(event.target.value)}
            placeholder="Search questions..."
            className="w-full rounded-md border border-gray-300 py-2 pl-9 pr-3 text-sm text-body focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
          />
        </div>
        {onAssignedToMeOnlyChange ? (
          <button
            type="button"
            onClick={() => onAssignedToMeOnlyChange(!assignedToMeOnly)}
            aria-pressed={assignedToMeOnly}
            className={twMerge(
              OUTLINE_BUTTON,
              "w-full",
              assignedToMeOnly &&
                "border-primary-500 bg-primary-50 text-primary-700 hover:bg-primary-50",
            )}
          >
            <UserCheck className="h-4 w-4" />
            Assigned to me
          </button>
        ) : null}
        <div className="grid grid-cols-2 gap-2">
          {onAddQuestion ? (
            <button type="button" onClick={onAddQuestion} className={OUTLINE_BUTTON}>
              <Plus className="h-4 w-4" />
              Add question
            </button>
          ) : null}
          {onSyncPending ? (
            <button
              type="button"
              onClick={onSyncPending}
              disabled={syncEligibleCount === 0 || isSyncing}
              className={OUTLINE_BUTTON}
              title={syncTitle}
            >
              <RefreshCw className={`h-4 w-4 ${isSyncing ? "animate-spin" : ""}`} />
              {isSyncing ? "Syncing..." : `Sync (${syncEligibleCount})`}
            </button>
          ) : null}
        </div>
        <div className="grid grid-cols-2 gap-2">
          {onAssignQuestions ? (
            <button
              type="button"
              onClick={onAssignQuestions}
              className={OUTLINE_BUTTON}
            >
              <Users className="h-4 w-4" />
              Assign
            </button>
          ) : null}
          {onExport ? (
            <button
              type="button"
              onClick={onExport}
              disabled={isExporting}
              className={PRIMARY_BUTTON}
              aria-busy={isExporting}
            >
              <Download className="h-4 w-4" />
              {isExporting ? "Exporting..." : "Export"}
            </button>
          ) : null}
        </div>
      </div>
    </header>
  );
};
